import { useForm, FormProvider } from 'react-hook-form'
import { useRef, useEffect, useState, useContext, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'
import Router from 'next/router'

// Context
import { FilterContext } from '../../context/FilterContext'
import { ModalContext } from '../../context/ModalContext'
import { NavigationContext } from '../../context/NavigationContext'
import { UserContext } from '../../context/UserContext'

// Internal components
import Button from '../Button/Button'
import FormItemAutocomplete from '../FormItem/FormItemAutocomplete'
import FormItemCheckbox from '../FormItem/FormItemCheckbox'
import FormItemDrawing from '../FormItem/FormItemDrawing'
import FormItemHidden from '../FormItem/FormItemHidden'
import FormItemNumber from '../FormItem/FormItemNumber'
import FormItemOptionlist from '../FormItem/FormItemOptionlist'
import FormItemOrderlist from '../FormItem/FormItemOrderlist'
import FormItemQuestion from '../FormItem/FormItemQuestion'
import FormItemRadiobuttons from '../FormItem/FormItemRadiobuttons'
import FormItemRange from '../FormItem/FormItemRange'
import FormItemRating from '../FormItem/FormItemRating'
import FormItemRoute from '../FormItem/FormItemRoute'
import FormItemSelect from '../FormItem/FormItemSelect'
import FormItemSpacer from '../FormItem/FormItemSpacer'
import FormItemTextarea from '../FormItem/FormItemTextarea'
import FormItemTextfield from '../FormItem/FormItemTextfield'
import FormItemFormation from '../FormItem/FormItemFormation'

import FormNav from '../FormNav/FormNav'
import FormValidation from '../FormValidation/FormValidation'
import Loader from '../Loader/Loader'

// Utils
import getSelectOptionsFiltered from '../../utils/data/getSelectOptionsFiltered'
import getDefaultSelectValues from '../../utils/data/getDefaultSelectValues'
import FormItemSwaplist from '../FormItem/FormItemSwaplist'
import { useMatchLog } from '@/root/context/MatchContext'
import { CalendarContext } from '@/root/context/CalendarContext'

const ModalForm = (props) => {
  const { items, columns, buttons, formdata } = props

  const formRef = useRef()
  const modalColumnRef = useRef()

  // Context
  const filterContext = useContext(FilterContext)
  const modalContext = useContext(ModalContext)
  const navigationContext = useContext(NavigationContext)
  const userContext = useContext(UserContext)
  const calendarContext = useContext(CalendarContext)
  const { refreshMatch } = useMatchLog()
  const MatchState = useMatchLog()

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // States
  const [isLoaded, setIsLoaded] = useState(false)
  const [revealState, setRevealState] = useState(false)
  const [groupedState, setGroupedState] = useState(formdata?.groups)
  const [responseState, setResponseState] = useState()
  const [fetchingState, setFetchingState] = useState(false)
  const [validationState, setValidationState] = useState(false)
  const [shouldValidateState, setShouldValidateState] = useState(true)
  const [alternativeRouteState, setAlternativeRouteState] = useState(false)
  const [formRouteState, setFormRouteState] = useState(false)
  const [filteringState, setFilteringState] = useState([])
  const [filteredState, setFilteredState] = useState({})
  const [enabledState, setEnabledState] = useState(formdata?.enabled)
  const [defaultFormValues] = useState(getDefaultSelectValues(items))

  const methods = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues
  })

  useEffect(() => {
    if (items) {
      setIsLoaded(true)
      setRevealStateOnLoad(items)
    }
  }, [])

  useEffect(() => {
    if (formdata.route && formdata.route !== formRouteState) {
      setFormRouteState(formdata.route)
    }
  }, [formdata.route])

  useEffect(() => {
    modalContext.setModalPrompt(formdata.prompt)
    if (formdata.prompt) modalContext.setModalCanPrompt(true)
  }, [formdata.prompt])

  useEffect(() => {
    const isNextPage = items?.filter((item) => item.type === 'route')?.length !== 0
    modalContext.setModalIsNextPage(isNextPage)
  }, [items])

  useEffect(() => {
    if (responseState?.reroute) {
      modalContext.setModalId(false)
      modalContext.setModalRoute(responseState.reroute)
      setFetchingState(false)
    }
    // Skip a step with data
    if (responseState?.autoSubmit) {
      setTimeout(() => {
        onFormRouteChange(
          responseState.autoSubmit?.[0]?.sections?.[0]?.elements?.find((obj) => obj.type === 'form')?.formdata?.route
        )
        setFetchingState(false)
      }, 1000)
    }

    if (responseState?.overwrite) {
      modalContext.setModalId(false)
      modalContext.setModalRoute(responseState.overwrite)
      setFetchingState(false)
    }

    if (responseState?.redirect) {
      setFetchingState(false)
      setResponseState(false)
      modalContext.setModalIsActive(false)
      Router.push(responseState?.redirect)
    }
    if (responseState?.refresh) {
      setFetchingState(false)
      setResponseState(false)
      modalContext.setModalIsActive(false)

      userContext.updateSession().then(() => {
        if (responseState?.currentPage) {
          if (window.location.pathname.includes('calendar')) {
            Router.push({ pathname: window.location.pathname }, { scroll: true })
            if (responseState.toast?.date) {
              calendarContext.setIndicatorDate(responseState.toast?.date)
              calendarContext.setScrollToast(true)
            }
          } else {
            Router.push({ pathname: responseState?.refresh, query: { id: responseState?.query?.id } }, { scroll: false })
          }
        } else if (responseState?.query) {
          Router.push({ pathname: responseState?.refresh, query: { id: responseState.query.id } }, { scroll: false })
        } else if (window.location.pathname === responseState?.refresh) {
          Router.reload()
        } else {
          Router.push(responseState?.refresh, { scroll: false })
        }
      })
    }
    if (responseState?.callback) {
      setTimeout(() => {
        setShouldValidateState(true)
        modalContext.setModalResponse(responseState.callback)
        setFetchingState(false)
      }, 100)
    }
    if (responseState?.toast) {
      if (responseState.toast.redirect)
        Router.replace(responseState.toast.redirect, responseState.toast.redirect, { scroll: false })
      if (responseState.toast.refresh) {
        refreshMatch(MatchState)

        if (responseState.toast?.currentPage) {
          if (window.location.pathname.includes('calendar')) {
            Router.push(
              { pathname: window.location.pathname, query: { id: responseState?.toast?.query?.id } },
              { scroll: true }
            )
            if (responseState.toast?.date) {
              calendarContext.setIndicatorDate(responseState.toast?.date)
              calendarContext.setScrollToast(true)
            }
          } else {
            Router.push(
              { pathname: responseState?.toast?.refresh, query: { id: responseState?.toast?.query?.id } },
              { scroll: false }
            )
          }
        } else if (responseState?.toast?.query) {
          Router.push(
            { pathname: responseState?.toast?.refresh, query: { id: responseState?.toast?.query?.id } },
            { scroll: false }
          )
        } else {
          Router.push(responseState?.toast?.refresh, { scroll: false })
        }
        userContext.updateSession()
      }
      setFetchingState(false)
      setResponseState(false)
      modalContext.setModalIsActive(false)
      navigationContext.setCardDialogIndex(false)
      navigationContext.setToastMessage(responseState.toast)
    }
    if (responseState?.selection) {
      if (responseState?.selection?.type === 'trainings') {
        filterContext.setCalendarTrainingSelection(responseState.selection)
      } else if (responseState?.selection?.type === 'exercises') {
        filterContext.setCalendarExerciseSelection(responseState.selection)
      }
      if (responseState.selection.redirect) Router.replace(responseState.selection.redirect)
    }
  }, [responseState])

  useEffect(() => {
    if (responseState?.modifier) modalContext.setModalModifier(responseState.modifier)
  }, [responseState?.modifier])

  const onFormItemChange = (params) => {
    const { itemProps } = params
    const itemTypesToInclude = ['select', 'radiobuttons', 'autocomplete', 'checkbox', 'rating']
    if (itemTypesToInclude.includes(itemProps.type) && itemProps.reveals) setRevealStateOnChange(params)
    if (itemProps.filters) setFilterStateOnChange(params)
    if (itemProps.enables) setEnabledStateOnChange(params)
    if (itemProps.type === 'optionlist') methods.handleSubmit(onSubmit)()
    if (itemProps.type === 'checkbox' && itemProps.requiredGroup) onGroupValidation(params)
  }

  const onFormItemLoad = (params) => {
    const { itemProps } = params
    const itemTypesToInclude = ['select', 'radiobuttons', 'autocomplete', 'checkbox', 'rating']
    if (itemProps.reveals && itemTypesToInclude.includes(itemProps.type)) setRevealStateOnChange(params)
    if (itemProps.filters) setFilterStateOnChange(params)
    if (itemProps.enables) setEnabledStateOnChange(params)
    if (formdata.groups) setGroupedState(formdata.groups)
  }

  const onGroupValidation = (params) => {
    let isValid = false
    const { itemProps } = params
    const groupRequired = itemProps?.requiredGroup ? itemProps?.requiredGroup : false
    if (groupRequired) {
      items.forEach((item) => {
        if (item?.requiredGroup === groupRequired && !isValid) isValid = methods.watch(item.name)
      })
    }
    setValidationState(isValid === false ? itemProps : false)
  }

  const onFormNavChange = ({ group }) => {
    const groupArr = []
    groupedState.forEach((groupItem) => {
      const groupObj = {}
      groupObj.label = groupItem.label
      groupObj.active = group.label === groupItem.label
      groupArr.push(groupObj)
    })
    setGroupedState(groupArr)
  }

  const onFormRouteChange = (route) => {
    if (route) setAlternativeRouteState(route)
  }

  useEffect(() => {
    if (alternativeRouteState) setTimeout(() => methods.handleSubmit(onSubmit)(), 100)
  }, [alternativeRouteState])

  const onFormRouteSkipValidate = (state) => {
    if (state) setShouldValidateState(false)
  }

  useEffect(() => {
    // Skip prompt if form hasn't been touched
    const { isDirty } = methods.formState
    modalContext.setModalIsDirty(isDirty)
  }, [methods.formState])

  const onSubmit = (data) => {
    if (!formdata.xhr || !formdata.route) return false
    fetchOnSubmit(data)
    setFetchingState(true)
    modalContext.setStateClass('is-submitting')
  }

  useEffect(() => {
    const filteredData = getSelectOptionsFiltered(filteringState, items)
    setFilteredState(filteredData)
  }, [filteringState])

  const setFilterStateOnChange = (params) => {
    const { itemProps, itemValue } = params
    const cache = { ...filteringState }
    cache[`${itemProps.name}`] = { itemProps, value: itemValue }
    setFilteringState(cache)
    methods.trigger()
  }

  const setEnabledStateOnChange = (params) => {
    const { itemProps } = params
    let hasEnabled = false
    let toEmpty = false
    const cache = enabledState
    const enabledItem = enabledState?.filter((input) => input.name === itemProps.name)
    const state = []
    if (enabledItem?.[0]?.name !== itemProps.name) return false
    cache.forEach((input) => {
      const isEnabled = input.name === itemProps.enables?.[0] || input.enabled
      const isLast = input.name === cache[parseInt(cache.length - 1)].name
      if (input.name === itemProps.enables?.[0] && !hasEnabled && !isLast) toEmpty = true
      state.push({
        name: input.name,
        label: input.label,
        enabled: isEnabled && !hasEnabled,
        toEmpty: toEmpty
      })
      if (input.name === itemProps.enables?.[0] && !hasEnabled) hasEnabled = true
    })
    setEnabledState(state)
  }

  const setRevealStateOnLoad = () => {
    const defaultRevealState = {}
    items.forEach((item) => {
      if (item.revealed && !defaultRevealState[item.revealed]) defaultRevealState[item.revealed] = false
      if (item.reveals && item.type === 'checkbox' && item.checked === true) defaultRevealState[item.name] = true
    })
    setRevealState(defaultRevealState)
  }

  const setRevealStateOnChange = useCallback(
    (params) => {
      setRevealState((revealState) => {
        const { itemProps, itemValue } = params
        const newState = { ...revealState }
        if (itemProps.type === 'select' || itemProps.type === 'radiobuttons') {
          const optionsList = itemProps.type === 'radiobuttons' ? itemProps.radios : itemProps.options
          const revealKeys = new Set(optionsList.filter((option) => option.reveals).map((option) => option.reveals))
          revealKeys.forEach((key) => {
            newState[key] = optionsList.some((option) => option.reveals === key && option.value === itemValue)
          })
        } else if (itemProps.type === 'checkbox') {
          newState[itemProps.name] = itemValue
        } else if (itemProps.type === 'rating') {
          const feedbackTypes = ['negative', 'neutral', 'positive']
          const getFeedbackType = (grade) => feedbackTypes[Math.ceil(grade / 2) - 1]
          newState[itemProps.name] = true
          // reset states
          newState[`${itemProps.name}`] = false
          for (const type of feedbackTypes) {
            newState[`${itemProps.name}--${type}`] = false
          }
          // set active state
          if (itemValue !== null) {
            newState[`${itemProps.name}`] = true
            newState[`${itemProps.name}--${getFeedbackType(itemValue)}`] = true
          }
        } else if (itemProps.type === 'autocomplete') {
          newState[itemProps.name] = itemValue.length >= 0
        }

        const state = { ...newState }
        for (const [option, value] of Object.entries(revealState)) {
          if (state[option] === undefined) state[option] = value
        }
        return state
      })
    },
    [revealState]
  )

  const fetchOnSubmit = async (data) => {
    fetch(alternativeRouteState || formdata.route, {
      method: 'POST',
      body: JSON.stringify(data)
    })
      .then((result) => result.json())
      .then((response) => {
        setResponseState(response)
        if (alternativeRouteState) {
          setAlternativeRouteState(false)
        }
      })
  }

  const checkKeyDown = (e) => {
    const keyCode = e.keyCode ? e.keyCode : e.which
    if (keyCode === 13) e.preventDefault()
  }

  const renderModalFormElements = (items, buttons, column = false) => {
    const elementList = []
    if (items?.length >= 1 && isLoaded === true) {
      items.forEach((item, index) => {
        if (column && column !== item.column) return false
        const formItems = renderModalFormItem(item, index)
        if (formItems) elementList.push(formItems)
      })
    }

    if (buttons?.length >= 1) {
      const buttonsGroup = renderModalFormButtons(buttons, column)
      if (buttonsGroup) elementList.push(buttonsGroup)
    }

    return elementList?.length >= 1 ? elementList : false
  }

  const renderModalFormButtons = (buttons, column = false) => {
    const buttonList = []
    buttons.forEach((button, index) => {
      if (!column || column === button.column) {
        const modalProps = button.type ? { modal: { type: button.type, modifier: '' } } : {}
        if (button.routePath) modalProps.modal.route = button.routePath
        if (button.routePath) modalProps.modal.id = button.routeId
        if (button.routeModifier && modalProps.modal) modalProps.modal.modifier = button.routeModifier

        const linkProps = button.link ? { link: { href: button.link } } : {}
        const iconProps = button.icon ? { icon: { id: button.icon } } : {}
        if (button.iconbefore === true) iconProps.before = true

        buttonList.push(
          <Button
            {...modalProps}
            {...linkProps}
            {...iconProps}
            disabled={
              button?.type === 'submit' &&
              Object.keys(methods.formState?.errors).length !== 0 &&
              ((!methods.formState.isValid && methods.formState.isSubmitted) || validationState !== false)
            }
            label={t(button.label)}
            modifiers={[button.modifiers]}
            key={`Button--${index}`}
          />
        )
      }
    })

    return (
      buttonList?.length >= 1 && (
        <div className="ModalForm-buttonGroup" key={`ModalForm-buttonGroup--${column || '0'}`}>
          {buttonList}
        </div>
      )
    )
  }

  const renderModalFormItem = (item, index) => {
    switch (item.type) {
      case 'autocomplete':
        return (
          <FormItemAutocomplete
            item={item}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'checkbox':
        return (
          <FormItemCheckbox
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            shouldValidateState={shouldValidateState}
            revealState={revealState[item.revealed] === true}
            onFormItemChange={onFormItemChange}
            onGroupValidation={onGroupValidation}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'drawing':
        return (
          <FormItemDrawing
            item={item}
            onFormItemLoad={onFormItemLoad}
            onFormItemChange={onFormItemChange}
            alternativeRoute={alternativeRouteState}
            columnRef={modalColumnRef}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'hidden':
        return (
          <FormItemHidden
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'number':
        return (
          <FormItemNumber
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'range':
        return (
          <FormItemRange
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'optionlist':
        return (
          <FormItemOptionlist
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            onFormItemChange={onFormItemChange}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'orderlist':
        return (
          <FormItemOrderlist
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'question':
        return (
          <FormItemQuestion
            item={item}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'radiobuttons':
        return (
          <FormItemRadiobuttons
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            shouldValidateState={shouldValidateState}
            revealState={revealState[item.revealed] === true}
            onFormItemChange={onFormItemChange}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'rating':
        return (
          <FormItemRating
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            onFormItemChange={onFormItemChange}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'route':
        return (
          <FormItemRoute
            item={item}
            onFormRouteChange={onFormRouteChange}
            onFormRouteSkipValidate={onFormRouteSkipValidate}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'select':
        return (
          <FormItemSelect
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            shouldValidateState={shouldValidateState}
            revealState={revealState[item.revealed] === true}
            onFormItemChange={onFormItemChange}
            filteredState={filteredState?.[item.name] ? filteredState[item.name] : false}
            enabledState={enabledState}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'spacer':
        return (
          <FormItemSpacer
            item={item}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'textarea':
        return (
          <FormItemTextarea
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            shouldValidateState={shouldValidateState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'textfield':
        return (
          <FormItemTextfield
            item={item}
            onFormItemLoad={onFormItemLoad}
            groupedState={groupedState}
            shouldValidateState={shouldValidateState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}-${item.name}`}
          />
        )
      case 'validation':
        return (
          <FormValidation
            item={item}
            validationState={validationState}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'formation':
        return (
          <FormItemFormation
            item={item}
            onFormItemLoad={onFormItemLoad}
            onFormItemChange={onFormItemChange}
            shouldValidateState={shouldValidateState}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      case 'swaplist':
        return (
          <FormItemSwaplist
            item={item}
            validationState={validationState}
            groupedState={groupedState}
            revealState={revealState[item.revealed] === true}
            key={`FormItem--${item.type}-${index}`}
          />
        )
      default:
        return ''
    }
  }

  return (
    <>
      <div className={`ModalForm${fetchingState ? ' is-fetching' : ''}`}>
        <FormProvider {...methods}>
          <form
            className="ModalForm-form"
            ref={formRef}
            onSubmit={methods.handleSubmit(onSubmit)}
            onKeyDown={(e) => checkKeyDown(e)}
            autoComplete="off"
          >
            {groupedState?.length >= 1 && <FormNav onFormNavChange={onFormNavChange} groupedState={groupedState} />}
            {columns?.length >= 1 &&
              columns.map((column, columnIndex) => (
                <div
                  ref={column === 'aside' ? modalColumnRef : null}
                  className={`ModalForm-column ModalForm-column--${column}`}
                  key={`ModalForm-column--${columnIndex}`}
                >
                  {renderModalFormElements(items, buttons, column)}
                </div>
              ))}
            {!columns && renderModalFormElements(items, buttons)}
            {modalContext.modalDialog && <div className="ModalForm-dialogOverlay" />}
          </form>
        </FormProvider>
      </div>
      {fetchingState && (
        <div className="ModalForm-loader">
          <Loader modifiers={['ModalForm-loadIcon']} />
        </div>
      )}
    </>
  )
}

ModalForm.propTypes = {
  formdata: PropTypes.object,
  items: PropTypes.array,
  columns: PropTypes.array,
  buttons: PropTypes.array
}

export default ModalForm
